import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Copy, Check } from "lucide-react";

/** The invitation fields needed to show its onboarding link. */
export interface CompanyInviteLink {
  invitationToken: string;
  companyName: string;
  expiresAt: string;
}

const getInviteLink = (token: string) => `${window.location.origin}/onboarding?token=${token}`;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

/**
 * Dialog body for an existing invitation: link with copy button, company and expiry.
 * Render inside a DialogContent. Shared by the create flow and the invitations table's "View invitation".
 */
export function InvitationDetails({ invitation, onDone }: { invitation: CompanyInviteLink; onDone: () => void }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const inviteLink = getInviteLink(invitation.invitationToken);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Copied!", description: "Invitation link copied to clipboard." });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-xl font-bold text-slate-900">Invitation Details</DialogTitle>
        <DialogDescription className="text-slate-500">
          Share this invitation link with the company admin to onboard their company.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label className="text-slate-700">Invitation Link</Label>
          <div className="flex gap-2">
            <Input value={inviteLink} readOnly className="font-mono text-sm bg-slate-50 rounded-2xl" />
            <Button size="icon" variant="outline" className="rounded-xl" onClick={copyToClipboard}>
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4 text-primary" />}
            </Button>
          </div>
        </div>
        <div className="p-4 bg-slate-50 rounded-2xl space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Company:</span>
            <span className="font-semibold text-slate-900">{invitation.companyName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Expires:</span>
            <span className="font-semibold text-slate-900">{formatDate(invitation.expiresAt)}</span>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button className="rounded-2xl" onClick={onDone}>Done</Button>
      </DialogFooter>
    </>
  );
}

export function CreateCompanyInviteDialog({
  apiBase,
  triggerLabel = "Create Invitation",
  onCreated,
}: {
  apiBase: string;
  triggerLabel?: string;
  onCreated?: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [createdInvitation, setCreatedInvitation] = useState<CompanyInviteLink | null>(null);

  const createMutation = useMutation({
    mutationFn: async (name: string): Promise<{ invitation: CompanyInviteLink }> => {
      const response = await fetch(`${apiBase}/invitations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ companyName: name }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create invitation");
      }
      return response.json();
    },
    onSuccess: (data) => {
      setCreatedInvitation(data.invitation);
      queryClient.invalidateQueries({ queryKey: [`${apiBase}/invitations`] });
      queryClient.invalidateQueries({ queryKey: [`${apiBase}/stats`] });
      toast({ title: "Invitation created", description: "The invitation token has been generated successfully." });
      onCreated?.();
    },
    onError: (error: Error) => {
      toast({ title: "Creation failed", description: error.message, variant: "destructive" });
    },
  });

  const handleCreate = () => {
    if (!companyName) {
      toast({ title: "Company name required", description: "Please enter a company name.", variant: "destructive" });
      return;
    }
    createMutation.mutate(companyName);
  };

  const handleClose = () => {
    setOpen(false);
    setCompanyName("");
    setCreatedInvitation(null);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => (isOpen ? setOpen(true) : handleClose())}>
      <DialogTrigger asChild>
        <Button className="rounded-2xl bg-primary hover:bg-primary/80 font-bold">
          <Plus className="h-4 w-4 mr-2" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] rounded-3xl">
        {createdInvitation ? (
          <InvitationDetails invitation={createdInvitation} onDone={handleClose} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900">Create Company Onboarding Invite</DialogTitle>
              <DialogDescription className="text-slate-500">
                Generate an invitation link for a company admin to create their workspace.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-slate-700">Company Name</Label>
                <Input
                  id="companyName"
                  type="text"
                  placeholder="e.g. Malawi Sun Energy Ltd."
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="bg-slate-50 rounded-2xl"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-2xl" onClick={handleClose}>Cancel</Button>
              <Button onClick={handleCreate} disabled={createMutation.isPending} className="rounded-2xl bg-primary hover:bg-primary/80 font-bold">
                {createMutation.isPending ? "Creating..." : "Create Token"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
